/**
 * Addresses for the rows of the catalog tree. An edit reloads the whole tree,
 * which unmounts the row that was edited and takes the reader's scroll position
 * with it, so a row has to be nameable: the name goes into the URL and is what
 * the page looks the row up by once the tree is back. Naming rows in the URL
 * also makes any of them linkable from outside.
 */
import type { TreeTopic } from "../api/domain.ts";

/** The kinds of row the tree renders, which is what can be addressed. */
export type AnchorKind = "topic" | "capability" | "question";

export type Anchor = { kind: AnchorKind; id: string };

const kinds: readonly AnchorKind[] = ["topic", "capability", "question"];

/**
 * The element id an anchor points at. The kind is part of it because a
 * capability and a question may carry the same id without colliding.
 */
export const anchorId = (anchor: Anchor): string => `${anchor.kind}-${anchor.id}`;

/** The `#…` addressing an anchor, escaped so an unusual id survives the URL. */
export const anchorHash = (anchor: Anchor): string =>
  `#${encodeURIComponent(anchorId(anchor))}`;

/** The anchor a `#…` names, or undefined when it names nothing this page holds. */
export const parseAnchor = (hash: string): Anchor | undefined => {
  const raw = decodeURIComponent(hash.startsWith("#") ? hash.slice(1) : hash);
  const kind = kinds.find((candidate) => raw.startsWith(`${candidate}-`));
  if (kind === undefined) return undefined;
  const id = raw.slice(kind.length + 1);
  return id === "" ? undefined : { kind, id };
};

/**
 * The topic a row sits under, which has to be unfolded before the row can be
 * seen. Undefined when no topic claims it, as for a capability naming a topic
 * that does not exist: those are listed outside the tree.
 */
export const anchorTopic = (
  topics: readonly TreeTopic[],
  anchor: Anchor,
): string | undefined => {
  if (anchor.kind === "topic") return anchor.id;
  const holder = topics.find((topic) =>
    anchor.kind === "capability"
      ? topic.capabilities.some((capability) => capability.id === anchor.id)
      : topic.questions.some((question) => question.id === anchor.id),
  );
  return holder?.id;
};

/**
 * Puts an addressed row back on screen. False when the row is not rendered,
 * which is the caller's cue to keep the anchor and try again after the next load.
 */
export const scrollToAnchor = (anchor: Anchor): boolean => {
  const element = document.getElementById(anchorId(anchor));
  if (element === null) return false;
  element.scrollIntoView?.({ block: "center" });
  return true;
};
